import { useState, useEffect, ChangeEvent, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import {
  addItemsToCart,
  updateCartItem,
  getNewPurchaseCategory,
  getAmcMasters,
  getNewPurchaseSchemes,
} from "../../../../../store/actions";
import { CartItems, Options } from "../../../../../store/types";
import { useAppSelector } from "../../../../../store/hooks";
import {
  convertToInr,
  getSelectedCartItem,
  handleError,
} from "../../../../../utils/cart";

import * as yup from "yup";

interface LocationState {
  cartId: string | number;
  cartItemId: string | number;
  amc: string;
}

interface SchemeOption {
  label: string;
  value: string;
  minAmount: number;
  multiples: number;
  scheme: string;
  plan: string;
  option: string;
  combo_flag?: string;
  combo_disclaimer?: string;
}

const FIELD_NAMES: Array<keyof Values> = [
  "amc",
  "category",
  "scheme",
  "amount",
];

export default function useNewPurchase() {
  const dispatch = useDispatch();
  const location = useLocation<LocationState>();
  const history = useHistory();

  const { state, pathname } = location;

  const isEditTransaction = pathname.includes("/edit-transaction");

  //   @ts-ignore
  const cartItems: CartItems<"NPL"> = useAppSelector(
    (state) => state.cartReducer.cartItems
  );

  const { payload } = getSelectedCartItem(cartItems, state);

  const [categoryOptions, setCategoryOptions] = useState<Options>([]);
  const [funds, setFunds] = useState<Options>([]);

  const [schemeOptions, setSchemeOptions] = useState<Array<SchemeOption>>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [initialValues, setInitialValues] = useState<Values>({
    amc: payload.fund,
    category: "",
    scheme: "",
    amount: "",
  });

  const cancelTransaction = () =>
    history.replace("/cart/investor-cart", {
      pan: payload.pan,
    });

  const resetLaterFields = useCallback(function (
    values: Values,
    key: keyof Values,
    fieldValues: Partial<Values>
  ): Values {
    const index = FIELD_NAMES.findIndex((fieldName) => fieldName === key);
    const updatedValues = FIELD_NAMES.slice(index).reduce<Partial<Values>>(
      (acc, curr) => {
        return { ...acc, [curr]: "" };
      },
      {}
    );

    return { ...values, ...updatedValues, ...fieldValues };
  },
  []);

  const handleCategoryChange: HandleChange = function (values, setValues) {
    return async function (e) {
      setValues(
        resetLaterFields(values, "category", {
          category: e.target.value,
        })
      );
      setSchemeOptions(
        await getNewPurchaseSchemes({
          fund: values.amc,
          schemetype: e.target.value,
        })(dispatch)
      );
    };
  };

  const handleComboSwitch: HandleCheckBoxChange = function (values, setValues) {
    return function (e, checked) {
      if (!checked) {
        setValues({ ...values, combo: false });
        return;
      }
      setValues({
        ...values,
        combo: window.confirm(
          "You have opted to do a Purchase cum Switch. Do you want to proceed?"
        ),
      });
    };
  };

  const handleSchemeChange: HandleChange = function (values, setValues) {
    return function (e) {
      const selectedScheme = schemeOptions.find(
        ({ value }) => value === e.target.value
      );
      setValues(
        resetLaterFields(values, "scheme", {
          scheme: e.target.value,
          minAmount: selectedScheme.minAmount,
          multiples: selectedScheme.multiples,
        })
      );
    };
  };

  async function onSubmit(values: Values) {
    try {
      const selectedScheme = schemeOptions.find(
        ({ value }) => values.scheme === value
      );
      const { label, option, plan, scheme } = selectedScheme;

      const requestBody = {
        ...payload,
        scheme,
        plan,
        option,
        amount: values.amount,
        category: values.category,
        schemeName: label,
        transactionType: "NPL",
        fundName: funds.find((f) => f.value === values.amc).label,
        combo: values.combo,
      };

      if (isEditTransaction) {
        const response = await updateCartItem({
          ...requestBody,
          cartId: state.cartId,
          cartItemId: state.cartItemId,
        })(dispatch);
        response && alert("Item has been updated successfully");
        history.replace("/cart/investor-cart", {
          pan: payload.pan,
        });
        return;
      }
      const response = await addItemsToCart(requestBody)(dispatch);
      if (!response) return;
      alert("Transaction added to cart successfully");
      history.replace("/cart/investor-cart", {
        pan: payload.pan,
      });
    } catch (error) {
      handleError(error);
    }
  }

  useEffect(() => {
    (async function () {
      setFunds(await getAmcMasters()(dispatch));
    })();
  }, [dispatch]);

  useEffect(() => {
    async function fetchInitialValues() {
      setCategoryOptions(await getNewPurchaseCategory(payload.fund)(dispatch));
      if (!isEditTransaction) return;

      const { amount, scheme, plan, option, category, fund } = payload;

      const initialSchemeOptions = await getNewPurchaseSchemes({
        fund,
        schemetype: category,
      })(dispatch);

      const selectedSchemeDetails = initialSchemeOptions.find((args) =>
        [
          args.plan === plan,
          args.scheme === scheme,
          args.option === option,
        ].every(Boolean)
      );

      setSchemeOptions(initialSchemeOptions);

      setInitialValues({
        amc: fund,
        amount,
        category,
        scheme: selectedSchemeDetails.value,
        minAmount: selectedSchemeDetails.minAmount,
        multiples: selectedSchemeDetails.multiples,
        combo: payload.combo,
      });
    }
    fetchInitialValues();
  }, [payload.fund, isEditTransaction, dispatch]);

  return {
    cancelTransaction,
    isEditTransaction,
    options: {
      categoryOptions,
      funds,
      schemeOptions,
    },
    handleCategoryChange,
    handleSchemeChange,
    handleComboSwitch,
    onSubmit,
    payload,
    initialValues,
    history,
    isModalOpen,
  };
}

export const validationSchema = yup.object().shape({
  amc: yup.string().required("AMC is required*"),
  category: yup.string().required("Category is required*"),
  scheme: yup.string().required("Scheme is required*"),
  amount: yup
    .string()
    .required("Investment Amount is required*")
    .test("minAmount", function (value) {
      const { minAmount = 500 } = this.parent;
      if (Number(value) < Number(minAmount)) {
        return this.createError({
          message: `Investment Amount should be greater than or equal to ${convertToInr(
            minAmount
          )}`,
        });
      }
      return true;
    })
    .test("multiples", function (value, { createError, parent }) {
      const { multiples = 1 } = parent;
      if (Number(value) % Number(multiples) !== 0) {
        createError({
          message: `Investment Amount should be a multiple of ${convertToInr(
            multiples
          )}`,
        });
      }
      return true;
    }),
});

interface OnChange extends ChangeEvent<{ name?: string; value: any }> {}

type HandleChange = (
  values: Values,
  setValues: React.Dispatch<React.SetStateAction<Values>>
) => (event: OnChange) => any;

interface Values {
  amc: string;
  category: string;
  scheme: string;
  amount: string;
  minAmount?: string | number;
  multiples?: string | number;
  combo?: boolean;
}

type HandleCheckBoxChange = (
  values: Values,
  setValues: React.Dispatch<React.SetStateAction<Values>>
) => (event: ChangeEvent<HTMLInputElement>, checked: boolean) => any;
